import { useState } from "react"
import { X, ChevronRight } from "lucide-react"
import {
  updateUserEmailActionReadOnlyService
} from "../../utils/userService"
import { getProviderDisplayName } from "../../utils/mailProvider"

const EmailPrefsModal = ({
  isOpen,
  onClose,
  onNavigateToInstructions,
  onNavigateToSchedule,
  onNavigateToFolders,
  readOnlyMode,
  setReadOnlyMode,
  showEmailTriageOnboardingModal,
  provider,
}) => {
  const providerName = getProviderDisplayName(provider)
  const [customInstructionsEnabled] = useState(true)
  const [customScheduleEnabled] = useState(true)
  const [customFoldersEnabled] = useState(true)

  const handleSaveReadOnlyMode = async (readOnlyMode) => {
    await updateUserEmailActionReadOnlyService({ enabled: readOnlyMode })
  }

  if (!isOpen) return null

  return (
    <div className="fixed inset-0 bg-black bg-opacity-75 flex justify-center items-center z-[1000]">
      <div className="bg-white p-8 rounded-lg w-[90%] max-w-[600px] shadow-md">
        <div className="flex justify-between items-center mb-6">
          <div className="flex flex-col gap-2">
            <h2 className="m-0 text-gray-800 flex items-center gap-2">
              Email Preferences
            </h2>
            <p className="text-gray-600 text-base">
              Configure how Geoffrey AI processes your email with custom instructions and scheduling.
            </p>
          </div>
          <button
            className="bg-transparent border-none hover:bg-gray-100 rounded-lg p-2 text-2xl cursor-pointer text-gray-600 hover:text-gray-800"
            onClick={onClose}
            data-amp-track-section="Email Preferences"
            data-amp-track-action="Close_Click"
            data-amp-track-description="Close Email Preferences"
          >
            <X size={24} />
          </button>
        </div>

        <div className="flex flex-col gap-6">

          {/* Read-Only Mode Toggle */}
          <div className="flex items-center justify-between p-4 border border-gray-200 rounded-lg">
            <div className="flex flex-col ml-3">
              <h3 className="flex items-center text-base font-semibold text-gray-800 mb-1">
                <span className="">Read Only Mode</span>
                <button className="text-xs text-sky-500 hover:text-sky-600 ml-2 bg-transparent border-none p-0 cursor-pointer font-normal hover:bg-transparent" 
                  onClick={() => {
                    onClose();
                    showEmailTriageOnboardingModal();
                  }}
                  data-amp-track-section="Email Preferences"
                  data-amp-track-action="Read_Only_Mode_What_Is_This_Click"
                  data-amp-track-description="Read Only Mode What's This"
                >
                  What's this?
                </button>
              </h3>
              <p className="text-sm text-gray-600">
                {readOnlyMode
                  ? `Geoffrey will NOT archive or move emails in your ${providerName} inbox.`
                  : `Geoffrey can archive and move emails in your ${providerName} inbox.`}
              </p>
            </div>
            <div className="ml-4">
              <label className="flex items-center cursor-pointer">
                <div className="relative">
                  <input
                    type="checkbox"
                    checked={readOnlyMode}
                    onChange={e => {
                      setReadOnlyMode(e.target.checked)
                      handleSaveReadOnlyMode(e.target.checked)
                    }}
                    className="sr-only"
                    data-amp-track-section="Email Preferences"
                    data-amp-track-action="Read_Only_Mode_Toggle"
                    data-amp-track-description="Toggle Read Only Mode"
                  />
                  <div
                    className={`w-12 h-6 pt-0.5 rounded-full transition-colors duration-200 ${
                      readOnlyMode ? "bg-sky-500" : "bg-gray-300"
                    }`}
                  >
                    <div
                      className={`w-5 h-5 bg-white rounded-full shadow-md transform transition-transform duration-200 ${
                        readOnlyMode ? "translate-x-6" : "translate-x-0.5"
                      }`}
                    ></div>
                  </div>
                </div>
                <span className="ml-2 text-sm text-gray-700">
                  {readOnlyMode ? "On" : "Off"}
                </span>
              </label>
            </div>
          </div>

          {/* Custom Instructions Option */}
          <div className="flex items-center justify-between p-2 border border-gray-200 rounded-lg">
            <div className="flex-1">
              <button
                onClick={customInstructionsEnabled ? onNavigateToInstructions : undefined}
                disabled={!customInstructionsEnabled}
                className={`flex items-center justify-between w-full text-left bg-transparent ${
                  customInstructionsEnabled ? "cursor-pointer hover:bg-gray-50" : "cursor-not-allowed opacity-50 hover:bg-transparent"
                } p-2 rounded transition-colors duration-200`}
                data-amp-track-section="Email Preferences"
                data-amp-track-action="Custom_Instructions_Click"
                data-amp-track-description="Custom Instructions"
              >
                <div className="flex flex-col w-full">
                  <h3 className="text-base font-semibold text-gray-800 mb-1">Custom Instructions</h3>
                  <p className="text-sm text-gray-600">
                    Provide Geoffrey with customized instructions on how to process your email.
                  </p>
                </div>
                {customInstructionsEnabled && <ChevronRight size={24} className="text-gray-400" />}
              </button>
            </div>
          </div>

          {/* Custom Schedule Option */}
          <div className="flex items-center justify-between p-2 border border-gray-200 rounded-lg">
            <div className="flex-1">
              <button
                onClick={customScheduleEnabled ? onNavigateToSchedule : undefined}
                disabled={!customScheduleEnabled}
                className={`flex items-center justify-between w-full text-left bg-transparent ${
                  customScheduleEnabled ? "cursor-pointer hover:bg-gray-50" : "cursor-not-allowed opacity-50 hover:bg-transparent"
                } p-2 rounded transition-colors duration-200`}
                data-amp-track-section="Email Preferences"
                data-amp-track-action="Custom_Schedule_Click"
                data-amp-track-description="Custom Schedule"
              >
                <div className="flex flex-col w-full">
                  <h3 className="text-base font-semibold text-gray-800 mb-1">
                    <span className="text-base font-semibold text-gray-800 mb-1 pr-2">Custom Schedule</span>
                  </h3>
                  <p className="text-sm text-gray-600">
                    Provide Geoffrey with custom rules on when to process your email.
                  </p>
                </div>
                {customScheduleEnabled && <ChevronRight size={24} className="text-gray-400" />}
              </button>
            </div>
          </div>

          {/* Custom Folders Option */}
          <div className="flex items-center justify-between p-2 border border-gray-200 rounded-lg">
            <div className="flex-1">
              <button
                onClick={customFoldersEnabled ? onNavigateToFolders : undefined}
                disabled={!customFoldersEnabled}
                className={`flex items-center justify-between w-full text-left bg-transparent ${
                  customFoldersEnabled ? "cursor-pointer hover:bg-gray-50" : "cursor-not-allowed opacity-50 hover:bg-transparent"
                } p-2 rounded transition-colors duration-200`}
                data-amp-track-section="Email Preferences"
                data-amp-track-action="Custom_Folders_Click"
                data-amp-track-description="Custom Folders"
              >
                <div className="flex flex-col w-full">
                  <h3 className="text-base font-semibold text-gray-800 mb-1">Custom Folders</h3>
                  <p className="text-sm text-gray-600">
                    Geoffrey routes mail into your system folders automatically. Add a custom folder to route specific emails there too.
                  </p>
                </div>
                {customFoldersEnabled && <ChevronRight size={24} className="text-gray-400" />}
              </button>
            </div>
          </div>
        </div>

        <div className="flex justify-end gap-2 mt-8">
          <button
            type="button"
            className="py-3 px-6 rounded-md text-base cursor-pointer transition-colors duration-200 bg-gray-100 border border-gray-300 text-gray-800 hover:bg-gray-200"
            onClick={onClose}
            data-amp-track-section="Email Preferences"
            data-amp-track-action="Close_Click"
            data-amp-track-description="Close Email Preferences"
          >
            Close
          </button>
        </div>
      </div>
    </div>
  )
}

export default EmailPrefsModal
